import { api } from "@/api/client";

/** Thin wrapper around the shared axios `api` instance (cookies + 401-refresh already work
 * generically for ANY role, including "client" — see api/client.js) — just namespaces the
 * Client Portal M1+M2 endpoints in one place. */
export const clientPortalApi = {
  requestLink: (email) => api.post("/v1/client-portal/auth/request-link", { email }),
  me: () => api.get("/v1/client-portal/me"),
  switchCompany: (companyRemoteId) => api.post("/v1/client-portal/auth/switch-company", { company_remote_id: companyRemoteId }),
  // Perf fix (2026-09, Client Portal audit finding): `ClientPortalHome.jsx`'s own mount effect
  // AND `OnboardingTourProvider` (wrapping the whole shell) both independently called
  // `GET /home` on first load — a genuine, if small, duplicate call the onboarding work
  // introduced. Deduped here, not by restructuring either caller: two near-simultaneous
  // `home()` calls collapse into one in-flight network request; a short-lived cached result
  // (3s) also absorbs the case where they're a beat apart, not perfectly simultaneous.
  _homeInFlight: null,
  _homeCache: null,
  home() {
    if (this._homeCache && Date.now() - this._homeCache.at < 3000) return Promise.resolve(this._homeCache.result);
    if (this._homeInFlight) return this._homeInFlight;
    this._homeInFlight = api.get("/v1/client-portal/home")
      .then((r) => { this._homeCache = { result: r, at: Date.now() }; return r; })
      .finally(() => { this._homeInFlight = null; });
    return this._homeInFlight;
  },
  listProjects: () => api.get("/v1/client-portal/projects"),
  getProject: (id) => api.get(`/v1/client-portal/projects/${id}`),
  getDeliverables: (id) => api.get(`/v1/client-portal/projects/${id}/deliverables`),
  getFilms: (id) => api.get(`/v1/client-portal/projects/${id}/films`),
  // M2 — Timeline (milestone rail), Updates + reactions, Documents, BTS gallery, and the rich
  // deliverable player (timestamped feedback + "Approve this cut").
  getTimeline: (id) => api.get(`/v1/client-portal/projects/${id}/timeline`),
  getUpdates: (id) => api.get(`/v1/client-portal/projects/${id}/updates`),
  reactToUpdate: (updateId) => api.post(`/v1/client-portal/updates/${updateId}/react`),
  getDocuments: (id) => api.get(`/v1/client-portal/projects/${id}/documents`),
  documentFileUrl: (projectId, docId) => `${api.defaults.baseURL}/v1/client-portal/projects/${projectId}/documents/${docId}/file`,
  getBts: (id) => api.get(`/v1/client-portal/projects/${id}/bts`),
  btsFileUrl: (projectId, mediaId) => `${api.defaults.baseURL}/v1/client-portal/projects/${projectId}/bts/${mediaId}/file`,
  getDeliverableFeedback: (projectId, deliverableId) => api.get(`/v1/client-portal/projects/${projectId}/deliverables/${deliverableId}/feedback`),
  addDeliverableFeedback: (projectId, deliverableId, body, timestampSeconds) =>
    api.post(`/v1/client-portal/projects/${projectId}/deliverables/${deliverableId}/feedback`, { body, timestamp_seconds: timestampSeconds }),
  approveDeliverable: (projectId, deliverableId, approved) =>
    api.post(`/v1/client-portal/projects/${projectId}/deliverables/${deliverableId}/approve`, { approved }),
  // M3 — Collaboration: Messages (async notes thread, polled, no websockets) + Meetings
  // (read-only client-visible summaries, never internal notes/transcript).
  getMessages: (id) => api.get(`/v1/client-portal/projects/${id}/messages`),
  sendMessage: (id, body) => api.post(`/v1/client-portal/projects/${id}/messages`, { body }),
  getMeetings: (id) => api.get(`/v1/client-portal/projects/${id}/meetings`),
  // M4 — Intelligence: hard-scoped read-only AI Assistant (per-project) + AI-guided New Project
  // Request (not tied to any project — see modules/client_ai.py + client_project_requests.py).
  askAi: (projectId, question) => api.post(`/v1/client-portal/projects/${projectId}/ai/ask`, { question }),
  submitProjectRequest: (rawText, preferredTimeline, locations) =>
    api.post("/v1/client-portal/project-requests", { raw_text: rawText, preferred_timeline: preferredTimeline, locations }),
  // Quick-Start Onboarding (2026-09) — first-login welcome + 4-step spotlight tour.
  getOnboardingStatus: () => api.get("/v1/client-portal/onboarding-status"),
  markOnboardingSeen: () => api.post("/v1/client-portal/onboarding-seen"),
  markOnboardingCompleted: () => api.post("/v1/client-portal/onboarding-completed"),
};
